import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import queryString from 'query-string';
import { Pagination, Response } from '@ui/src';
import { clientsApi } from './service';
import { Client } from '../types';
import { LockAuditLog, Lock } from '../../security/types';

import ROUTES from '../../../routes';

export interface ClientsState {
  clients?: Client[];
  client?: Client;
  pagination?: Pagination;
  locks?: Lock[];
  locksPagination?: Pagination;
  lockItem?: Lock;
  auditLogs?: LockAuditLog[];
  auditLogsPagination?: Pagination;
  lockItemAuditLogs?: LockAuditLog[];
  lockItemAuditLogsPagination?: Pagination;
  activeLock?: Lock;
  errorsList?: string;
}

const initialState: ClientsState = {};

export const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    clearPanelState: (state) => {
      state.client = undefined;
      state.errorsList = undefined;
      state.locks = undefined;
      state.locksPagination = undefined;
      state.auditLogs = undefined;
      state.auditLogsPagination = undefined;
      state.activeLock = undefined;
    },
    clearLockPanelState: (state) => {
      state.lockItem = undefined;
      state.lockItemAuditLogs = undefined;
      state.lockItemAuditLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      clientsApi.endpoints.getClients.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      clientsApi.endpoints.getClients.matchRejected,
      (state, action: any) => {
        state.clients = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      clientsApi.endpoints.getClients.matchFulfilled,
      (state, action: PayloadAction<Response<Client>>) => {
        state.clients = action?.payload?.data?.edges?.map((item) => ({
          ...item.node,
          ids: item.node.id,
          url: generatePath(ROUTES.CLIENTS_EDIT, {
            clientId: item.node.id,
          }),
        }));
        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      clientsApi.endpoints.getClient.matchFulfilled,
      (state, action: PayloadAction<{ data: Client }>) => {
        state.client = action.payload.data;
      },
    );
    builder.addMatcher(
      clientsApi.endpoints.getClientLockItem.matchFulfilled,
      (state, action: PayloadAction<{ data: Lock }>) => {
        state.lockItem = action.payload.data;
      },
    );
    builder.addMatcher(
      clientsApi.endpoints.updateClientLock.matchFulfilled,
      (state, action: PayloadAction<{ data: Lock }>) => {
        state.locks = state.locks?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = {
              ...result,
              ...action.payload.data,
            };
          }

          return result;
        });

        state.lockItem = action.payload.data;
      },
    );
    builder.addMatcher(
      clientsApi.endpoints.getClientLocks.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<Lock>> & {
          meta: { arg: { originalArgs?: string } };
        },
      ) => {
        state.locks = action.payload.data.edges.map((item) => ({
          ...item.node,
          type: item.node.createdByEmployeeId ? 'by Employee' : 'Auto',
        }));

        state.locksPagination = action.payload.data.pageInfo;

        if (action.meta?.arg?.originalArgs) {
          const parsed = queryString.parse(action.meta?.arg?.originalArgs);

          if (
            parsed.locked &&
            parsed.locked === 'true' &&
            state.activeLock &&
            !action.payload.data.edges.length
          ) {
            state.activeLock = undefined;
          }
        }
      },
    );
    builder.addMatcher(
      clientsApi.endpoints.getClientActiveLocks.matchFulfilled,
      (state, action: PayloadAction<Response<Lock>>) => {
        if (action?.payload?.data?.edges?.length) {
          state.activeLock = action.payload.data.edges[0].node;
        } else {
          state.activeLock = undefined;
        }
      },
    );
    builder.addMatcher(
      clientsApi.endpoints.getClientAuditLogs.matchFulfilled,
      (state, action: PayloadAction<Response<LockAuditLog>>) => {
        state.auditLogs = action.payload.data.edges.map((item) => item.node);

        state.auditLogsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      clientsApi.endpoints.getClientLockItemAuditLogs.matchFulfilled,
      (state, action: PayloadAction<Response<LockAuditLog>>) => {
        if (state.lockItem) {
          state.lockItemAuditLogs = action.payload.data.edges.map(
            (item) => item.node,
          );

          state.lockItemAuditLogsPagination = action.payload.data.pageInfo;
        }
      },
    );
  },
});

export const { clearPanelState, clearLockPanelState } = clientsSlice.actions;

export default clientsSlice.reducer;
